import React, { useState } from 'react';
import styled from 'styled-components';
import tw from 'twin.macro';
import { useMutation, useQuery } from '@apollo/client';
import { toast } from 'react-toastify';
import { Button } from '../../components/button';
import { Modal } from '../../components/modal';
import { Badge, statusTone } from '../../components/ui';
import {
  ADMIN_CANCEL_BOOKING_FOR_PAUSED_HOST,
  BRAND_HOSTS,
  HOST_OPEN_BOOKINGS_ON_BRAND,
} from '../../services/brandService/queries';
import { formatInstantInZone } from '../../helpers/timezoneHelpers';
import { graphqlErrorMessage, TripLike } from '../../utils/listingHold';
import { openTripsOf, pausedHostTripAction } from '../../utils/pausedHostTrips';

interface HostTrip extends TripLike {
  id: string;
  guest?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  car?: {
    name?: string | null;
    year?: number | null;
    make?: string | null;
    model?: string | null;
    location?: { timezone?: string | null } | null;
  } | null;
}

const Title = styled.h2`
  ${tw`text-lg font-semibold mb-1`}
  color: var(--color-text-primary);
`;
const Muted = styled.p`
  ${tw`text-sm mb-3`}
  color: var(--color-text-secondary);
`;
const TripRow = styled.div`
  ${tw`flex flex-wrap items-center justify-between gap-2 p-2 rounded-md mb-2 text-sm`}
  border: 1px solid var(--color-border);
`;
const TripText = styled.div`
  ${tw`flex flex-col gap-0.5 min-w-0`}
  color: var(--color-text-primary);
`;
const TripMeta = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;
const Hint = styled.span`
  ${tw`text-xs`}
  color: var(--color-text-secondary);
`;

const carLabel = (car: HostTrip['car']) =>
  `${car?.year ?? ''} ${car?.make ?? ''} ${car?.model ?? ''}`.trim() || car?.name || 'Car';

/**
 * Paused host (D41): a host's open trips on this brand. Booked trips go ahead
 * when the host is paused; the admin may cancel a future one with a full refund.
 */
export function HostTripsDialog({
  userId,
  hostLabel,
  hostPaused,
  onClose,
}: {
  userId: string;
  hostLabel: string;
  hostPaused: boolean;
  onClose: () => void;
}) {
  const { data, loading, error, refetch } = useQuery<{ hostOpenBookingsOnBrand: HostTrip[] }>(
    HOST_OPEN_BOOKINGS_ON_BRAND,
    { variables: { userId }, fetchPolicy: 'network-only' },
  );
  const [cancelTrip] = useMutation(ADMIN_CANCEL_BOOKING_FOR_PAUSED_HOST, {
    refetchQueries: [{ query: BRAND_HOSTS }],
  });
  const [busy, setBusy] = useState<string | null>(null);
  const now = Date.now();
  const trips = openTripsOf(data?.hostOpenBookingsOnBrand ?? [], now);

  const cancel = async (trip: HostTrip) => {
    if (
      !window.confirm(
        `Cancel this trip with a full refund? ${hostLabel} and the guest are emailed and notified.`,
      )
    ) {
      return;
    }
    setBusy(trip.id);
    try {
      await cancelTrip({ variables: { bookingId: trip.id } });
      toast.success('Trip cancelled and refunded');
      await refetch();
    } catch (err) {
      toast.error(graphqlErrorMessage(err, 'Could not cancel the trip'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal isOpen onClose={onClose} title={`Trips of ${hostLabel}`}>
      <div data-testid="host-trips-dialog">
        <Title>Trips of {hostLabel}</Title>
        <Muted>
          Booked trips on this brand that have not ended. Pausing the host keeps them; a future trip
          can be cancelled here with a full refund once the host is paused.
        </Muted>
        {loading && <Muted>Loading…</Muted>}
        {error && <Muted role="alert">{graphqlErrorMessage(error, error.message)}</Muted>}
        {!loading && !error && trips.length === 0 && <Muted>No open trips.</Muted>}
        {trips.map(trip => {
          const action = pausedHostTripAction(trip, hostPaused, now);
          const zone = trip.car?.location?.timezone?.trim() || 'America/Los_Angeles';
          const when = (ms: number) => formatInstantInZone(ms, zone, 'MMM d, yyyy h:mm a');
          const guest =
            [trip.guest?.firstName, trip.guest?.lastName].filter(Boolean).join(' ') ||
            trip.guest?.email ||
            'Guest';
          return (
            <TripRow key={trip.id} data-testid={`host-trip-${trip.id}`}>
              <TripText>
                <span>
                  {carLabel(trip.car)} · {guest}
                </span>
                <TripMeta>
                  {when(trip.startAt)} → {when(trip.endAt)}
                </TripMeta>
                <span>
                  <Badge tone={statusTone(trip.status)} size="sm" dot>
                    {trip.status.replace(/_/g, ' ')}
                  </Badge>
                </span>
              </TripText>
              {action === 'cancel' && (
                <Button
                  theme="danger"
                  size="sm"
                  type="button"
                  text="Cancel with full refund"
                  isLoading={busy === trip.id}
                  isDisabled={Boolean(busy)}
                  onClick={() => void cancel(trip)}
                  dataTestId={`host-trip-cancel-${trip.id}`}
                />
              )}
              {action === 'pauseFirst' && (
                <span title="Pause the host first">
                  <Button
                    theme="secondary"
                    size="sm"
                    type="button"
                    text="Cancel with full refund"
                    isDisabled
                    dataTestId={`host-trip-cancel-${trip.id}`}
                  />{' '}
                  <Hint>Pause the host first</Hint>
                </span>
              )}
              {action === 'inProgress' && <Hint>In progress, goes ahead</Hint>}
            </TripRow>
          );
        })}
      </div>
    </Modal>
  );
}

export default HostTripsDialog;
